import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, Zap, ShieldAlert, Wrench, Cpu, Activity, X,
} from "lucide-react";
import { Nav, Footer } from "@/components/Chrome";
import { AccountGate } from "@/components/AccountGate";
import { Brain3D } from "@/components/Brain3D";
import { Showcase } from "@/components/Showcase";
import { auditPrompt, billingStatus } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PLATFORMS = ["Lovable", "Bolt", "Replit", "Emergent", "Cursor", "v0", "Other"];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Honest activity labels — describes what CoreText is doing, no fake % or
// claimed-complete backend stages.
const ANALYSIS_STEPS = [
  "Reading your prompt…",
  "Mapping intent & requirements…",
  "Scoring readiness…",
  "Composing a tighter prompt…",
  "Finalizing your audit…",
];
const AnalysisStatus = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => Math.min(n + 1, ANALYSIS_STEPS.length - 1)), 2200);
    return () => clearInterval(t);
  }, []);
  return <span data-testid="analysis-status-text">{ANALYSIS_STEPS[i]}</span>;
};

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [platform, setPlatform] = useState(() => localStorage.getItem("coretext_last_platform") || "Emergent");
  const [loading, setLoading] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);
  const promptRef = useRef(null);

  const changePlatform = (p) => {
    setPlatform(p);
    localStorage.setItem("coretext_last_platform", p); // persist manual selection as preference
  };

  useEffect(() => {
    if (!user) { setSubStatus(null); return; }
    let active = true;
    billingStatus()
      .then((s) => { if (active) setSubStatus(s?.status || null); })
      .catch(() => { if (active) setSubStatus(null); });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const pp = localStorage.getItem("ct_pending_prompt");
    if (pp) {
      setPrompt(pp);
      const pl = localStorage.getItem("ct_pending_platform");
      if (pl) setPlatform(pl);
      localStorage.removeItem("ct_pending_prompt");
      localStorage.removeItem("ct_pending_platform");
      toast.success("You're signed in — hit Audit to continue.");
    }
  }, [user]);

  // Re-run from History: prefill original prompt + platform, but never auto-run.
  useEffect(() => {
    const st = location.state;
    if (st && st.prefillPrompt) {
      setPrompt(st.prefillPrompt);
      if (st.prefillPlatform) setPlatform(st.prefillPlatform); // historical platform, not saved as preference
      window.history.replaceState({}, "");
      toast.message("Loaded from History — review the platform, then run.");
      // Bring the restored input into view and focus it (do NOT auto-run).
      setTimeout(() => {
        if (promptRef.current) {
          promptRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          promptRef.current.focus();
        }
      }, 150);
    }
  }, [location.state]);

  const runAudit = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const audit = await auditPrompt(prompt, platform, controller.signal);
      // Ignore a stale response if a newer request started or this one was cancelled.
      if (myReqId !== reqIdRef.current) return;
      navigate(`/audit/${audit.id}`);
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return; // cancelled: not an error
      if (myReqId !== reqIdRef.current) return;
      const status = err?.response?.status;
      if ((status === 403 || status === 429) && !user) {
        toast.error(err?.response?.data?.detail || "You've hit the free limit.");
        setShowGate(true);
      } else if (status === 429) {
        toast.error(err?.response?.data?.detail || "Rate limit reached. Try again shortly.");
      } else {
        toast.error(err?.response?.data?.detail || "Audit failed. Try again.");
      }
      setLoading(false);
    }
  };

  const cancelAnalysis = () => {
    reqIdRef.current++; // invalidate any in-flight response
    if (abortRef.current) abortRef.current.abort();
    setLoading(false);
    toast.message("Analysis cancelled. Your prompt and platform are still here.");
  };

  const onSubmit = async () => {
    if (!prompt.trim()) {
      toast.error("Paste a prompt first.");
      return;
    }
    // The server enforces the 3-analyses/24h anonymous limit; runAudit opens the
    // account gate on a 429/403 so free users can create an account and continue.
    runAudit();
  };

  return (
    <div className="relative text-[var(--text)]">
      <Nav />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen pt-16 overflow-hidden">
        {/* Live 3D brain backdrop (desktop) */}
        <div className="absolute inset-0">
          <Brain3D className="hidden lg:block absolute inset-0 w-full h-full" active={loading} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05060a]/20 via-transparent to-[#05060a]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05060a] via-[#05060a]/10 to-[#05060a]/50" />
        </div>
        <div className="absolute inset-0 grid-bg" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-8 md:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center pointer-events-none">
          {/* Mobile-only live 3D brain */}
          <div className="lg:hidden relative order-first pointer-events-auto">
            <div className="relative h-44 sm:h-60 rounded-2xl overflow-hidden border border-[var(--line)]">
              <Brain3D className="absolute inset-0 w-full h-full" active={loading} />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#05060a] via-transparent to-transparent" />
            </div>
          </div>

          {/* Left copy */}
          <div className="lg:col-span-6">
            <motion.div variants={fade} custom={0} initial="hidden" animate="show"
              className="inline-flex items-center gap-2 rounded-full border border-neon-cyan/30 glass px-4 py-1.5 font-mono text-[11px] tracking-[0.2em] text-neon-cyan mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse-glow" />
              THE BUILD INTELLIGENCE LAYER
            </motion.div>

            <motion.h1 variants={fade} custom={1} initial="hidden" animate="show"
              className="font-display font-bold tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-[4.1rem] text-white">
              Rebuild as you go{" "}
              <span className="text-neon-cyan text-glow-cyan">is over.</span>
            </motion.h1>

            <motion.p variants={fade} custom={2} initial="hidden" animate="show"
              className="font-mono text-sm md:text-base text-[var(--muted)] mt-6 leading-relaxed max-w-lg">
              CoreText understands what you're trying to build, catches what you
              forgot, and learns from the corrections that caused your previous
              rebuilds — <em className="text-neon-cyan not-italic">before</em> you send
              your next instruction to AI.
            </motion.p>

            <motion.div variants={fade} custom={3} initial="hidden" animate="show"
              className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs text-[var(--muted)]">
              {[
                { icon: Cpu, t: "Intent Mapping" },
                { icon: ShieldAlert, t: "Missing Requirement Detection" },
                { icon: Activity, t: "Personalized Build Intelligence" },
              ].map((f) => (
                <span key={f.t} className="inline-flex items-center gap-2">
                  <f.icon size={15} className="text-neon-cyan" /> {f.t}
                </span>
              ))}
            </motion.div>

            <motion.p variants={fade} custom={4} initial="hidden" animate="show"
              className="font-mono text-xs text-[var(--muted)] mt-6 leading-relaxed max-w-lg">
              Fewer corrections. Fewer rebuilds.{" "}
              <span className="text-white">Less wasted time, tokens, and credits.</span>
            </motion.p>
          </div>

          {/* Right — input console */}
          <motion.div
            variants={fade} custom={2} initial="hidden" animate="show"
            className="lg:col-span-6 pointer-events-auto">
            <div className="glass neon-border rounded-2xl p-6 md:p-7 relative overflow-hidden scanlines">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-neon-red" />
                  <span className="w-2.5 h-2.5 rounded-full bg-neon-yellow" />
                  <span className="w-2.5 h-2.5 rounded-full bg-neon-green" />
                </div>
                <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--muted)]">
                  coretext://audit
                </span>
              </div>

              <label className="font-display font-semibold text-lg tracking-tight block text-white">
                Paste the prompt you're about to send
              </label>
              <p className="font-mono text-xs text-[var(--muted)] mt-1 mb-4">
                Exactly as you'd send it. Coretext flags what's missing.
              </p>

              <textarea
                ref={promptRef}
                data-testid="prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={9}
                placeholder="> Build me a booking app for my salon. Make it look nice and let people pick a time."
                className="w-full bg-[#070910] border border-neon-cyan/25 rounded-xl px-4 py-3 font-mono text-sm text-neon-cyanBright placeholder:text-[var(--muted)] resize-none focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors leading-relaxed"
              />

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                <div className="sm:col-span-2">
                  <label className="font-mono text-[11px] text-[var(--muted)] block mb-1.5 tracking-wider">
                    TARGET PLATFORM
                  </label>
                  <select
                    data-testid="platform-select"
                    value={platform}
                    onChange={(e) => changePlatform(e.target.value)}
                    disabled={loading}
                    className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg px-3 py-3 font-mono text-sm text-white focus:outline-none focus:border-neon-cyan appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p} className="bg-[#0b0d14]">{p}</option>
                    ))}
                  </select>
                </div>
                {loading ? (
                  <div className="sm:col-span-3 flex items-center gap-2">
                    <div data-testid="analysis-status" className="flex-1 flex items-center gap-2 rounded-lg border border-neon-cyan/30 bg-[#070910] py-3.5 px-4 font-mono text-sm text-neon-cyan">
                      <Loader2 size={18} className="animate-spin shrink-0" />
                      <AnalysisStatus />
                    </div>
                    <button
                      data-testid="cancel-analysis-button"
                      onClick={cancelAnalysis}
                      className="rounded-lg font-display font-bold py-3.5 px-4 inline-flex items-center gap-2 border border-neon-red/40 text-neon-red hover:bg-neon-red/10 transition-colors"
                    >
                      <X size={16} /> Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="audit-button"
                    onClick={onSubmit}
                    style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
                    className="sm:col-span-3 group relative z-10 rounded-lg font-display font-bold tracking-wide py-3.5 px-6 flex items-center justify-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200"
                  >
                    INSPECT MY PROMPT <ArrowRight size={18} strokeWidth={2.5} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
              <p className="font-mono text-[11px] text-[var(--muted)] mt-3" data-testid="audit-entitlement-note">
                {user && user.is_admin
                  ? "CoreText Owner · Unlimited"
                  : user && subStatus === "trialing"
                    ? "CoreText Pro · Trial active"
                    : user && subStatus === "active"
                      ? "CoreText Pro · Active"
                      : "First 3 audits free · No signup to start · Powered by Claude Sonnet 4.6"}
              </p>
            </div>
          </motion.div>
        </div>

        {/* marquee */}
        <div className="relative border-y border-[var(--line)] py-3 overflow-hidden">
          <div className="flex gap-12 whitespace-nowrap animate-marquee font-mono text-xs tracking-[0.25em] text-[var(--muted)]">
            {["a", "b"].map((row) => (
              <span key={row} className="flex gap-12">
                {["LOVABLE", "BOLT", "REPLIT", "EMERGENT", "CURSOR", "v0", "•", "AUDIT BEFORE YOU SEND", "•", "SAVE YOUR CREDITS"].map((t, i) => (
                  <span key={`${row}-${t}-${i}`} className={t === "•" ? "text-neon-cyan" : ""}>{t}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SHOWCASE WALL ===== */}
      <Showcase />

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="max-w-2xl mb-12">
          <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan mb-3">// HOW CORETEXT SAVES YOU CREDITS</p>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-white leading-tight">
            One vague prompt can cost you five rebuild loops.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldAlert, t: "Catch vague asks", d: "No acceptance criteria means the AI invents a generic app you'll spend credits reshaping message by message." },
            { icon: Wrench, t: "Lock the scope", d: "Say what NOT to touch. Stop the AI from rewriting unrelated parts of your app and breaking what already worked." },
            { icon: Zap, t: "Ship it tightened", d: "Get a rewritten, copy-paste-ready prompt engineered to land in one shot instead of five." },
          ].map((f, i) => (
            <motion.div key={f.t} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="glass rounded-2xl p-7 relative overflow-hidden group hover:border-neon-cyan/40 transition-colors duration-300">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl border border-neon-cyan/40 mb-5 group-hover:glow-cyan transition-shadow">
                <f.icon size={22} className="text-neon-cyan" />
              </span>
              <h3 className="font-display font-bold text-xl text-white mb-2">{f.t}</h3>
              <p className="font-mono text-sm text-[var(--muted)] leading-relaxed">{f.d}</p>
              <span className="absolute -bottom-8 -right-8 font-display font-bold text-8xl text-white/[0.03] select-none">0{i + 1}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== BIG STAT BAND ===== */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pb-8">
        <div className="glass neon-border rounded-3xl px-8 md:px-14 py-14 relative overflow-hidden text-center">
          <div className="absolute inset-0 grid-bg opacity-60" />
          <p className="relative font-mono text-xs tracking-[0.3em] text-neon-amber mb-4">THE HARD TRUTH</p>
          <p className="relative font-display font-bold text-3xl md:text-5xl tracking-tight text-white leading-tight max-w-4xl mx-auto">
            That's <span className="text-neon-cyan text-glow-cyan">5×</span> the credits
            to arrive somewhere you could've reached in a single, complete prompt.
          </p>
          <p className="relative font-mono text-sm text-[var(--muted)] mt-5 max-w-xl mx-auto">
            Coretext reads your prompt like the machine will — and tells you exactly
            where it's about to guess.
          </p>
        </div>
      </section>

      <Footer />

      <AccountGate
        open={showGate}
        prompt={prompt}
        platform={platform}
        onClose={() => setShowGate(false)}
        onAuthed={() => {
          setShowGate(false);
          runAudit();
        }}
      />
    </div>
  );
}
